import React, { useState } from "react";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { FactorCards } from "../components/kfm/Factors";
import { BasisSpark, Overline, StockHeader, ThesisStrip } from "../components/kfm/Shared";
import { SCREENER } from "../components/kfm/data";
import VariantOdds from "../components/kfm/VariantOdds";
import VariantClock from "../components/kfm/VariantClock";
import VariantLedger from "../components/kfm/VariantLedger";
import VariantPeers from "../components/kfm/VariantPeers";
import VariantTripwires from "../components/kfm/VariantTripwires";
import RiaBookXray from "../components/kfm/personas/RiaBookXray";
import RiaOnePager from "../components/kfm/personas/RiaOnePager";
import RiaBrief from "../components/kfm/personas/RiaBrief";
import HfSeasonDigest from "../components/kfm/personas/HfSeasonDigest";
import HfCrowding from "../components/kfm/personas/HfCrowding";
import HfEarningsWeek from "../components/kfm/personas/HfEarningsWeek";
import { PainBanner } from "../components/kfm/personas/PersonaUi";
import AllocFees from "../components/kfm/personas/AllocFees";
import AllocSkill from "../components/kfm/personas/AllocSkill";
import AllocLookthrough from "../components/kfm/personas/AllocLookthrough";
import AllocMonitor from "../components/kfm/personas/AllocMonitor";
import RiaSecondOpinion from "../components/kfm/personas/RiaSecondOpinion";
import RiaHousehold from "../components/kfm/personas/RiaHousehold";
import RiaFundJustify from "../components/kfm/personas/RiaFundJustify";
import RiaModelMonitor from "../components/kfm/personas/RiaModelMonitor";

/* KENSYS FACTOR MODEL — product mockups (sample DELL story; Odds/Clock use real historical base rates). */

const GROUPS = [
  { id: "stock", label: "Stock page", first: "a" },
  { id: "ria", label: "RIA", first: "ria-second" },
  { id: "hf", label: "HF analyst", first: "hf-digest" },
  { id: "alloc", label: "Allocator", first: "alloc-fees" },
];

const VARIANTS = {
  stock: [
    { id: "current", label: "Current", hint: "price vs basis chart" },
    { id: "a", label: "A · Odds", hint: "what usually happens" },
    { id: "b", label: "B · Trade clock", hint: "alpha delivered vs typical" },
    { id: "c", label: "C · Ledger", hint: "who is in, at what cost" },
    { id: "d", label: "D · Peer map", hint: "best expression?" },
    { id: "e", label: "E · Tripwires", hint: "what changes the view" },
    { id: "screener", label: "Cross-section", hint: "all names" },
  ],
  ria: [
    { id: "ria-second", label: "Funds · Prospect second opinion", hint: "win the prospect in 2 minutes" },
    { id: "ria-household", label: "Funds · Household look-through", hint: "what the client really owns" },
    { id: "ria-justify", label: "Funds · Fund one-pager", hint: "why this fund is worth its fee" },
    { id: "ria-models", label: "Funds · Model monitor", hint: "which fund to replace, who it touches" },
    { id: "ria-xray", label: "Stocks · Book X-ray", hint: "which holdings need attention" },
    { id: "ria-onepager", label: "Stocks · Client one-pager", hint: "why do we own it" },
    { id: "ria-brief", label: "Stocks · Monday brief", hint: "what changed that matters" },
  ],
  hf: [
    { id: "hf-digest", label: "Filing-season digest", hint: "what changed in my coverage" },
    { id: "hf-crowding", label: "Crowding & exit risk", hint: "who sells first" },
    { id: "hf-earnings", label: "Earnings-week planner", hint: "where is the edge into prints" },
  ],
  alloc: [
    { id: "alloc-fees", label: "Fee check", hint: "active fees for an index portfolio?" },
    { id: "alloc-skill", label: "Skill or style", hint: "selection vs factor tilts" },
    { id: "alloc-look", label: "Lineup look-through", hint: "8 managers or 3?" },
    { id: "alloc-monitor", label: "Watch-list monitor", hint: "fiduciary quarterly review" },
  ],
};

const Current = () => (
  <div className="space-y-4" data-testid="kfm-variant-current">
    <StockHeader />
    <Card data-testid="kfm-chart-card"><CardContent className="pt-4">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
        <Overline>Price vs institutional cost basis</Overline>
        <div className="text-[11px] text-muted-foreground">estimated from 13F cohort build-quarter prices · disclosure-timed</div>
      </div>
      <BasisSpark height={280} labels />
    </CardContent></Card>
    <ThesisStrip />
    <FactorCards prefix="kfm" />
  </div>
);

const Screener = () => (
  <Card data-testid="kfm-screener-card"><CardContent className="pt-4">
    <Overline className="mb-1">Cross-section — nightly scores, all covered equities</Overline>
    <p className="text-[11px] text-muted-foreground mb-3">~2,700 US names · verdicts derive from score + active overlays, never from a single factor</p>
    <table className="w-full text-xs" data-testid="kfm-screener-table">
      <thead className="border-b border-border text-muted-foreground">
        <tr>{["Ticker", "Verdict", "Score", "Confirmation", "New funds 2q", "Skilled exits", "Earnings", "Sector"].map((h, i) => (
          <th key={h} className={`py-1.5 px-2 ${i >= 2 && i <= 6 ? "text-right" : "text-left"}`}>{h}</th>))}</tr>
      </thead>
      <tbody>
        {SCREENER.map((r) => (
          <tr key={r.sym} className="border-b border-border/40 hover:bg-muted/30" data-testid={`kfm-row-${r.sym}`}>
            <td className="py-2 px-2 font-mono font-bold">{r.sym}</td>
            <td className={`py-2 px-2 font-bold ${r.vcol}`}>{r.verdict}</td>
            <td className="py-2 px-2 text-right font-mono">{r.score}</td>
            <td className="py-2 px-2 text-right font-mono">{r.vs}</td>
            <td className="py-2 px-2 text-right font-mono">{r.nm}</td>
            <td className={`py-2 px-2 text-right font-mono ${r.lv !== "0" ? "text-amber-400" : "text-muted-foreground"}`}>{r.lv}</td>
            <td className={`py-2 px-2 text-right font-mono ${r.earn.includes("RED") ? "text-red-400" : r.earn.includes("GREEN") ? "text-emerald-400" : ""}`}>{r.earn}</td>
            <td className="py-2 px-2 text-muted-foreground">{r.sector}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </CardContent></Card>
);

const STOCK_PAINS = {
  current: ["ANY INVESTOR · BASIS CHECK", "Am I paying more or less than the professionals who just bought?", "price drawn against the pros' estimated cost"],
  a: ["PM / RIA · ODDS", "If I act on this signal, what usually happens — and how bad can it get?", "outcomes of 139 past look-alike setups"],
  b: ["PM · THESIS TRACKING", "I'm already in — is the trade playing out as expected, or stalling?", "alpha delivered vs the typical path, day 47 of 126"],
  c: ["ANALYST · DUE DILIGENCE", "Who exactly is buying, how good are they at this sector, and what did they pay?", "named-fund ledger with skill, cost and P&L"],
  d: ["PM · IDEA SELECTION", "I like the sector call — is this the best stock to express it, or is there a better one?", "peer map: thesis confirmation vs model alpha"],
  e: ["PM / RIA · MONITORING", "What would make me change my mind — and how close are we to it?", "six tripwires with distance to trigger"],
  screener: ["ANALYST · SCREENING", "Across ~2,700 names, where should I look first today?", "nightly cross-section ranked by verdict"],
};

const VIEWS = {
  current: Current, a: VariantOdds, b: VariantClock, c: VariantLedger, d: VariantPeers, e: VariantTripwires, screener: Screener,
  "ria-xray": RiaBookXray, "ria-onepager": RiaOnePager, "ria-brief": RiaBrief,
  "ria-second": RiaSecondOpinion, "ria-household": RiaHousehold, "ria-justify": RiaFundJustify, "ria-models": RiaModelMonitor,
  "hf-digest": HfSeasonDigest, "hf-crowding": HfCrowding, "hf-earnings": HfEarningsWeek,
  "alloc-fees": AllocFees, "alloc-skill": AllocSkill, "alloc-look": AllocLookthrough, "alloc-monitor": AllocMonitor,
};

export default function KfmMockupPage() {
  const [group, setGroup] = useState("stock");
  const [view, setView] = useState("a");
  const View = VIEWS[view];
  const pickGroup = (g) => { setGroup(g.id); setView(g.first); };
  return (
    <div className="space-y-5" data-testid="kfm-mockup-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex rounded-md border border-border p-0.5" data-testid="kfm-persona-switcher">
            {GROUPS.map((g) => (
              <button key={g.id} onClick={() => pickGroup(g)} data-testid={`kfm-group-${g.id}`}
                className={`px-3 py-1.5 rounded text-xs font-bold tracking-wide transition-colors ${group === g.id ? "bg-sky-500/20 text-sky-300" : "text-muted-foreground hover:text-foreground"}`}>
                {g.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="kfm-variant-switcher">
            {VARIANTS[group].map((v) => (
              <button key={v.id} onClick={() => setView(v.id)} data-testid={`kfm-view-${v.id}`} title={v.hint}
                className={`px-3 py-1.5 rounded text-xs font-medium border transition-colors ${view === v.id ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                {v.label}
              </button>
            ))}
          </div>
        </div>
        <Badge variant="outline" className="border-amber-500/50 text-amber-400" data-testid="kfm-mockup-banner">
          MOCKUP — SAMPLE DATA
        </Badge>
      </div>
      {group === "stock" && STOCK_PAINS[view] && (
        <PainBanner testid={`kfm-pain-${view}`} persona={STOCK_PAINS[view][0]} pain={STOCK_PAINS[view][1]} answer={STOCK_PAINS[view][2]} />
      )}
      <View />
    </div>
  );
}
